import React, { useState, useEffect } from "react";
import nanoporeSignalUi from "../assets/images/basehugger_plot.png";
import qualityControlUi from "../assets/images/cloud_pipeline_ui.png";
import cloudPipelineUi from "../assets/images/quality_control_ui.png";

const SciencePreview = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [resetTimer, setResetTimer] = useState(0);
  const totalSlides = 3;

  useEffect(() => {
    const timer = setInterval(() => {
      setIsAutoScrolling(true);
      setIsTransitioning(true);
      setCurrentIndex((prev) => prev + 1);
    }, 7000); // Wait 7 seconds before scrolling
    return () => clearInterval(timer);
  }, [resetTimer]);

  const handleManualAction = (action) => {
    setIsAutoScrolling(false);
    setIsTransitioning(true);
    action();
    setResetTimer((t) => t + 1); // Restarts the interval
  };

  const handleNext = () => {
    handleManualAction(() => {
      setCurrentIndex((prev) => {
        if (prev >= totalSlides) return prev;
        return prev + 1;
      });
    });
  };

  const handlePrev = () => {
    handleManualAction(() => {
      if (currentIndex <= 0) {
        setIsTransitioning(false);
        setCurrentIndex(totalSlides);
        setTimeout(() => {
          setIsTransitioning(true);
          setCurrentIndex(totalSlides - 1);
        }, 50);
      } else {
        setCurrentIndex((prev) => prev - 1);
      }
    });
  };

  const handleTransitionEnd = () => {
    if (currentIndex >= totalSlides) {
      setIsTransitioning(false);
      setCurrentIndex(0);
    }
  };

  return (
    <section id="science" className="science-section">
      <div className="container">
        <div className="science-inner">
          <div className="science-text">
            <h2 className="science-title">
              Rigorous analysis methods, built in
            </h2>
            <p className="science-body">
              FeraBio's pipelines are grounded in advanced computational methods for
              nanopore signal processing, consensus basecalling, and
              modification detection. Every result includes quality metrics and
              statistical controls so you can trust what you publish.
            </p>
            <ul className="science-list">
              <li>High-accuracy basecalling with configurable model selection</li>
              <li>Built-in quality control and signal validation</li>
              <li>Statistical thresholds for modification and variant calls</li>
            </ul>
          </div>

          <div className="science-visual">
            <div className="discrete-carousel">

              <div className="carousel-viewport">
                <div
                  className="carousel-track"
                  onTransitionEnd={handleTransitionEnd}
                  style={{
                    transform: `translateX(calc(-${(currentIndex % (totalSlides + 1)) * 100}%))`,
                    transition: isTransitioning
                      ? (isAutoScrolling ? "transform 1.2s ease-in-out" : "transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)")
                      : "none"
                  }}
                >
                  {/* Slide 1 */}
                  <div className="carousel-slide">
                    <div className="slide-card">
                      <div className="slide-visual-box p-0" style={{ overflow: 'hidden' }}>
                        <img src={nanoporeSignalUi} alt="Nanopore signal UI" className="mockup-img" style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', objectFit: 'cover', transform: 'scale(1.01)' }} />
                      </div>
                      <div className="slide-content-box">
                        <div className="slide-stat-block">
                          <span className="stat-val">Analysis Engine</span>
                          <span className="stat-desc">Machine Learning Decoding</span>
                        </div>
                        <h3 className="slide-card-title">Modified Basecalling</h3>
                        <p className="slide-card-desc">Deploy custom models to accurately basecall UBP XNAs and epigenetic modifications directly from the raw nanopore signal.</p>
                      </div>
                    </div>
                  </div>

                  {/* Slide 2 */}
                  <div className="carousel-slide">
                    <div className="slide-card">
                      <div className="slide-visual-box p-0" style={{ overflow: 'hidden' }}>
                        <img src={qualityControlUi} alt="Quality score bar plot" className="mockup-img" style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', objectFit: 'cover', transform: 'scale(1.15)' }} />
                      </div>
                      <div className="slide-content-box">
                        <div className="slide-stat-block">
                          <span className="stat-val">Data Validation</span>
                          <span className="stat-desc">Interactive QA Dashboard</span>
                        </div>
                        <h3 className="slide-card-title">Rich Data Interpretations</h3>
                        <p className="slide-card-desc">Explore rich, fully interactive outputs to effortlessly interpret your nanopore data. Click through detailed read-level quality metrics, alignments, and compositions to validate your sequencing runs.</p>
                      </div>
                    </div>
                  </div>

                  {/* Slide 3 */}
                  <div className="carousel-slide">
                    <div className="slide-card">
                      <div className="slide-visual-box p-0" style={{ overflow: 'hidden' }}>
                        <img src={cloudPipelineUi} alt="Basecalling module dashboard" className="mockup-img" style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', objectFit: 'cover', transform: 'scale(1.15)' }} />
                      </div>
                      <div className="slide-content-box">
                        <div className="slide-stat-block">
                          <span className="stat-val">Platform UI</span>
                          <span className="stat-desc">Zero-Code Pipeline Execution</span>
                        </div>
                        <h3 className="slide-card-title">Easy-to-Use Workflows</h3>
                        <p className="slide-card-desc">Intuitive, point-and-click module settings let anyone configure advanced pipelines without touching a command line. Select your models, set filters, and run analysis instantly.</p>
                      </div>
                    </div>
                  </div>

                  {/* Clone of Slide 1 for infinite circular scrolling */}
                  <div className="carousel-slide" aria-hidden="true">
                    <div className="slide-card">
                      <div className="slide-visual-box p-0" style={{ overflow: 'hidden' }}>
                        <img src={nanoporeSignalUi} alt="Nanopore signal UI" className="mockup-img" style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', objectFit: 'cover', transform: 'scale(1.01)' }} />
                      </div>
                      <div className="slide-content-box">
                        <div className="slide-stat-block">
                          <span className="stat-val">Analysis Engine</span>
                          <span className="stat-desc">Machine Learning Decoding</span>
                        </div>
                        <h3 className="slide-card-title">Modified Basecalling</h3>
                        <p className="slide-card-desc">Deploy custom models to accurately basecall UBP XNAs and epigenetic modifications directly from the raw nanopore signal.</p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              <div className="carousel-controls-bottom">
                <button className="control-btn" onClick={handlePrev}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" /></svg>
                </button>
                <div className="carousel-dots">
                  {[0, 1, 2].map((idx) => (
                    <button
                      key={idx}
                      className={`dot ${currentIndex % totalSlides === idx ? 'active' : ''}`}
                      onClick={() => handleManualAction(() => setCurrentIndex(idx))}
                      aria-label={`Go to slide ${idx + 1}`}
                    />
                  ))}
                </div>
                <button className="control-btn" onClick={handleNext}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SciencePreview;
